import React, { useEffect, useState, useMemo, useRef } from "react";
import { getCurrencySymbol } from '../utils/currency';
import { FaTimes, FaTrash, FaChevronUp, FaChevronDown } from "react-icons/fa";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
} from "firebase/firestore";
import { deleteUserDocument, writeUserDocument } from "@services/firebaseUser";
import {
  readGlobalProfiles,
  fetchCombinations,
  readFlatTemplate,
} from "@services/firebaseGlobal";
import { getFirebase, getProdDb } from "@services/firebase";
import { useAlert } from '../contexts/ToastContext';
import EnquiryProfileCard from "@components/EnquiryProfileCard";
import EnquiryLargeCard from "@components/EnquiryLargeCard";
import {
  markDocAsSeen,
  BUYER_PAGE_RESET_STATES,
  SUPER_PAGE_RESET_STATES,
} from "@hooks/useEnquiryBadge";
import "./css/Enquiry.css";

const Enquiry = ({ userRole: propUserRole }) => {
  const { db, auth } = getFirebase();
  const [user] = useAuthState(auth);
  const showAlert = useAlert();

  const [enquiries, setEnquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [oldOrders, setOldOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [userRole, setUserRole] = useState(() => {
    // Use prop if available, otherwise try localStorage
    if (propUserRole) return propUserRole;
    try {
      const cachedRole = localStorage.getItem("userRole");
      return cachedRole || "";
    } catch (err) {
      console.error("Error reading userRole from localStorage:", err);
      return "";
    }
  });
  const [superExclusionStates, setSuperExclusionStates] = useState([]);
  const [adminExclusionStates, setAdminExclusionStates] = useState([]);
  const [moverExclusionStates, setMoverExclusionStates] = useState([]);
  const [userExclusionStates, setUserExclusionStates] = useState([]);
  const [otimelineData, setOtimelineData] = useState([]);
  const [orderReceivedIdNumber, setOrderReceivedIdNumber] = useState(null);
  const [processControls, setProcessControls] = useState([]);
  const [exceptionTimelines, setExceptionTimelines] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [userNameMap, setUserNameMap] = useState({});
  const [showProcessFlow, setShowProcessFlow] = useState(false);
  const [expandedStatuses, setExpandedStatuses] = useState(null);

  // Page-level badge reset: mark docs as seen when this page is visited,
  // for states where opening the page is enough (no need to open the card).
  const pageResetDoneRef = useRef(false);
  useEffect(() => {
    if (pageResetDoneRef.current) return;
    const allDocs = [...enquiries, ...orders, ...oldOrders];
    if (!allDocs.length) return;
    pageResetDoneRef.current = true;
    const pageResetStates =
      userRole === "Super" ? SUPER_PAGE_RESET_STATES : BUYER_PAGE_RESET_STATES;
    allDocs.forEach((doc) => {
      if (pageResetStates.has(doc.reqState))
        markDocAsSeen(doc.id, doc.reqState);
    });
  }, [enquiries, orders, oldOrders, userRole]);

  useEffect(() => {
    if ((userRole === "Super" || userRole === "Mover") && db) {
      Promise.all([
        getDocs(collection(getProdDb(), "profile")),
        readGlobalProfiles("cUsers"),
      ])
        .then(([snap, userList]) => {
          const envMap = {};
          userList.forEach((u) => { envMap[u.id] = u.env || 'Prod'; });
          const map = {};
          snap.docs.forEach((d) => {
            const pd = d.data();
            map[d.id] = {
              name: [pd.f_name, pd.l_name].filter(Boolean).join(" ") || "",
              email: pd.email || "",
              env: envMap[d.id] || 'Prod',
            };
          });
          setUserNameMap(map);
        })
        .catch(() => {});
    }
  }, [userRole, db]);

  useEffect(() => {
    if (!user || !db) {
      setLoading(false);
      return;
    }

    const fetchUserRole = async () => {
      try {
        const userList = await readGlobalProfiles("cUsers");
        const currentUserData = userList.find(
          (userData) => userData.id === user.uid
        );
        if (currentUserData) {
          let role = "User";
          if (currentUserData.isadminuser) {
            role = "Admin";
          } else if (currentUserData.issuperuser) {
            role = "Super";
          } else if (currentUserData.isbuyer) {
            role = "Buyer";
          } else if (currentUserData.ismover) {
            role = "Mover";
          }
          setUserRole(role);
          // Sync to localStorage
          try {
            localStorage.setItem("userRole", role);
          } catch (err) {
            console.error("Error saving userRole to localStorage:", err);
          }
        } else {
          setUserRole("");
        }
      } catch (err) {
        console.error("Error fetching user role:", err);
        setUserRole("");
      }
    };

    // Only fetch if we don't have a role from prop
    if (!propUserRole) {
      fetchUserRole();
    } else {
      // Role is provided via props
      setUserRole(propUserRole);
    }
  }, [user, propUserRole]);

  useEffect(() => {
    if (!user || !db) return;

    const fetchProcessControlData = async () => {
      try {
        const processControls = await fetchCombinations("oprscontrol");
        setProcessControls(processControls);
        const superExclusions = processControls
          .filter(
            (control) =>
              control.id.startsWith("super") &&
              control.application === "Page" &&
              control.activity === "Exclude"
          )
          .map((control) => control.state);
        setSuperExclusionStates(superExclusions);

        const adminExclusions = processControls
          .filter(
            (control) =>
              control.id.startsWith("admin") &&
              control.application === "Page" &&
              control.activity === "Exclude"
          )
          .map((control) => control.state);
        setAdminExclusionStates(adminExclusions);

        const moverExclusions = processControls
          .filter(
            (control) =>
              control.id.startsWith("mover") &&
              control.application === "Page" &&
              control.activity === "Exclude"
          )
          .map((control) => control.state);
        setMoverExclusionStates(moverExclusions);

        const userExclusions = processControls
          .filter(
            (control) =>
              control.id.startsWith("buyer") &&
              control.application === "Page" &&
              control.activity === "Exclude"
          )
          .map((control) => control.state);
        setUserExclusionStates(userExclusions);
        try {
          const et = await fetchCombinations("oexptimeline");
          setExceptionTimelines(et);
        } catch (_) {
          setExceptionTimelines([]);
        }
      } catch (err) {
        console.error("Error fetching process control data:", err);
        setSuperExclusionStates([]);
        setAdminExclusionStates([]);
        setMoverExclusionStates([]);
        setUserExclusionStates([]);
      }
    };

    fetchProcessControlData();
  }, [user]);

  useEffect(() => {
    if (!user || !db) return;

    const fetchOtimelineData = async () => {
      try {
        const data = await readFlatTemplate("otimeline");
        setOtimelineData(data);
        const orderReceivedEntry = data.find(
          (entry) => entry.name === "Order Received"
        );
        if (orderReceivedEntry) {
          const idNumber = parseInt(
            orderReceivedEntry.id.replace("id", ""),
            10
          );
          setOrderReceivedIdNumber(idNumber);
        }
      } catch (err) {
        console.error("Error fetching otimeline data:", err);
      }
    };

    fetchOtimelineData();
  }, [user]);

  // Memoized function to filter enquiries efficiently
  const filterEnquiriesByRole = useMemo(() => {
    return (
      enquiries,
      userRole,
      otimelineData,
      orderReceivedIdNumber,
      exclusionStates
    ) => {
      const filteredEnquiries = enquiries.filter((enquiry) => {
        const currentState = enquiry.reqState || "New Request";
        if (currentState === "Request Cancelled") return true;
        if (!otimelineData || otimelineData.length === 0) return true;
        const stateEntry = otimelineData.find(
          (entry) => entry.name === currentState
        );
        if (!stateEntry) return true;
        const stateIdNumber = parseInt(stateEntry.id.replace("id", ""), 10);
        return stateIdNumber < orderReceivedIdNumber;
      });

      return filteredEnquiries.filter(
        (enquiry) =>
          !exclusionStates.includes(enquiry.reqState || "New Request")
      );
    };
  }, []);

  useEffect(() => {
    if (!user || !db || !userRole || !orderReceivedIdNumber) {
      return;
    }

    let qEnquiry;
    if (userRole === "Super" || userRole === "Admin" || userRole === "Mover") {
      qEnquiry = query(collection(db, "enquiry"));
    } else {
      qEnquiry = query(
        collection(db, "enquiry"),
        where("userId", "==", user.uid)
      );
    }

    const unsubscribeEnquiry = onSnapshot(
      qEnquiry,
      (snapshot) => {
        const updatedEnquiries = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        let exclusionStates;
        if (userRole === "Super") {
          exclusionStates = superExclusionStates;
        } else if (userRole === "Admin") {
          exclusionStates = adminExclusionStates;
        } else if (userRole === "Mover") {
          exclusionStates = moverExclusionStates;
        } else {
          exclusionStates = userExclusionStates;
        }

        const finalEnquiries = filterEnquiriesByRole(
          updatedEnquiries,
          userRole,
          otimelineData,
          orderReceivedIdNumber,
          exclusionStates
        );
        setEnquiries(finalEnquiries);
        setLoading(false);
      },
      (err) => {
        console.error("Error listening to enquiry:", err);
        setEnquiries([]);
        setLoading(false);
      }
    );

    return () => unsubscribeEnquiry();
  }, [
    user,
    userRole,
    superExclusionStates,
    adminExclusionStates,
    moverExclusionStates,
    userExclusionStates,
    otimelineData,
    orderReceivedIdNumber,
  ]);

  useEffect(() => {
    if (
      !user ||
      !db ||
      !userRole ||
      otimelineData.length === 0 ||
      !orderReceivedIdNumber
    ) {
      return;
    }

    let qOrder;
    if (userRole === "Super" || userRole === "Admin" || userRole === "Mover") {
      qOrder = query(collection(db, "order"));
    } else {
      qOrder = query(collection(db, "order"), where("userId", "==", user.uid));
    }

    const unsubscribeOrder = onSnapshot(
      qOrder,
      (snapshot) => {
        const updatedOrders = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const filteredOrders = updatedOrders.filter((order) => {
          const currentState = order.reqState || "Order Received";
          if (currentState === "Request Cancelled") return false;
          const stateEntry = otimelineData.find(
            (entry) => entry.name === currentState
          );
          if (!stateEntry) return false;
          const stateIdNumber = parseInt(stateEntry.id.replace("id", ""), 10);
          return stateIdNumber >= orderReceivedIdNumber;
        });

        let finalOrders = filteredOrders;
        if (userRole === "Super") {
          finalOrders = filteredOrders.filter(
            (order) =>
              !superExclusionStates.includes(order.reqState || "Order Received")
          );
        } else if (userRole === "Admin") {
          finalOrders = filteredOrders.filter(
            (order) =>
              !adminExclusionStates.includes(order.reqState || "Order Received")
          );
        } else if (userRole === "Mover") {
          finalOrders = filteredOrders.filter(
            (order) =>
              !moverExclusionStates.includes(order.reqState || "Order Received")
          );
        } else if (userRole === "Buyer") {
          finalOrders = filteredOrders.filter(
            (order) =>
              !userExclusionStates.includes(order.reqState || "Order Received")
          );
        }

        setOrders(finalOrders);
        setLoading(false);
      },
      (err) => {
        console.error("Error listening to order:", err);
        setOrders([]);
        setLoading(false);
      }
    );

    return () => unsubscribeOrder();
  }, [
    user,
    userRole,
    otimelineData,
    superExclusionStates,
    adminExclusionStates,
    moverExclusionStates,
    userExclusionStates,
    orderReceivedIdNumber,
  ]);

  useEffect(() => {
    if (
      !user ||
      !db ||
      !userRole ||
      otimelineData.length === 0 ||
      !orderReceivedIdNumber
    )
      return;
    let qOldOrder;
    if (userRole === "Super" || userRole === "Admin" || userRole === "Mover") {
      qOldOrder = query(collection(db, "oldOrder"));
    } else {
      qOldOrder = query(
        collection(db, "oldOrder"),
        where("userId", "==", user.uid)
      );
    }
    const unsubscribeOldOrder = onSnapshot(
      qOldOrder,
      (snapshot) => {
        let docs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        docs = docs.filter((o) => !o.s_delete);
        if (userRole === "Super") {
          docs = docs.filter(
            (o) => !superExclusionStates.includes(o.reqState || "Give Feedback")
          );
        } else if (userRole === "Admin") {
          docs = docs.filter(
            (o) => !adminExclusionStates.includes(o.reqState || "Give Feedback")
          );
        } else if (userRole === "Mover") {
          docs = docs.filter(
            (o) => !moverExclusionStates.includes(o.reqState || "Give Feedback")
          );
        } else if (userRole === "Buyer") {
          docs = docs.filter(
            (o) => !userExclusionStates.includes(o.reqState || "Give Feedback")
          );
        }
        setOldOrders(docs);
      },
      (err) => {
        console.error("Error listening to oldOrder:", err);
        setOldOrders([]);
      }
    );
    return () => unsubscribeOldOrder();
  }, [
    user,
    userRole,
    otimelineData,
    superExclusionStates,
    adminExclusionStates,
    moverExclusionStates,
    userExclusionStates,
    orderReceivedIdNumber,
  ]);

  const handleDelete = async (docId, collectionName, ownerId) => {
    try {
      if (collectionName === "oldOrder") {
        // Mark as deleted in DB but preserve the archived document
        if (selectedProfileId === docId) setSelectedProfileId(null);
        await writeUserDocument(
          "oldOrder",
          ownerId || user.uid,
          { s_delete: true },
          docId
        );
        return;
      }
      await deleteUserDocument(collectionName, ownerId || user.uid, docId);
      if (selectedProfileId === docId) {
        setSelectedProfileId(null);
      }
      if (collectionName === "enquiry") {
        setEnquiries((prev) => prev.filter((profile) => profile.id !== docId));
      } else if (collectionName === "order") {
        setOrders((prev) => prev.filter((profile) => profile.id !== docId));
      }
    } catch (err) {
      console.error(`Error deleting from ${collectionName}:`, err);
      showAlert(`Failed to delete profile: ${err.message}`);
    }
  };

  const handleProfileClick = (profile) => {
    const isOpening = selectedProfileId !== profile.id;
    setSelectedProfileId(isOpening ? profile.id : null);
    if (isOpening) markDocAsSeen(profile.id, profile.reqState);
  };

  const handleProfileMoved = (profileId) => {
    setEnquiries((prev) => prev.filter((profile) => profile.id !== profileId));
  };

  const renderProfileCard = (profile, collectionName) => {
    const firstTier =
      profile.tiers && profile.tiers.length > 0 ? profile.tiers[0] : {};
    const flavour = profile.cflavour || firstTier.cflavour || "Unknown";
    const filling = profile.cfilling || firstTier.cfilling || "None";
    const sponge = firstTier.sponge || "unknown";
    const isExpanded = selectedProfileId === profile.id;

    return (
      <div key={profile.id} className="enquiry-profile-card-wrapper">
        <EnquiryProfileCard
          theme={profile.theme || " "}
          subtitle={filling}
          flavour={flavour}
          cover={profile.cover || "none"}
          eggless={profile.eggless || false}
          glutenFree={profile.glutenFree || false}
          sponge={sponge}
          quantity={profile.quantity || null}
          ccost={profile.ccost || "N/A"}
          costEstimate={profile.costest || null}
          image={
            profile.images && profile.images.length > 0 ? profile.images[0] : ""
          }
          deliveryDate={profile.deliveryDate || ""}
          deliveryTime={profile.deliveryTime || ""}
          address={profile.del_address || ""}
          del_mode={profile.del_mode !== undefined ? profile.del_mode : true}
          rev_feed={profile.rev_feed || ""}
          showDeleteIcon={true}
          onDelete={() => handleDelete(profile.id, collectionName)}
          onClick={() => handleProfileClick(profile)}
          onProfileMoved={handleProfileMoved}
          isExpanded={isExpanded}
          userId={user.uid}
          userRole={userRole}
          enquiryId={profile.id}
          collectionName={collectionName}
        />
        {isExpanded && (
          <div className="enquiry-expanded-details">
            <EnquiryLargeCard
              profile={profile}
              user={user}
              userRole={userRole}
              enquiryId={profile.id}
              collectionName={collectionName}
              onClose={() => setSelectedProfileId(null)}
              onProfileMoved={handleProfileMoved}
            />
          </div>
        )}
      </div>
    );
  };

  const rolePrefixForUser = (userRole || "buyer").toLowerCase();

  const canEnableActionForProfile = (profile) => {
    const rs = profile.reqState || "New Request";
    return processControls.some(
      (c) =>
        c.id.startsWith(rolePrefixForUser) &&
        c.application === "Small" &&
        c.activity === "Action" &&
        c.state === rs
    );
  };

  const canEnableDeleteForProfile = (profile) => {
    const rs = profile.reqState || "New Request";
    return processControls.some(
      (c) =>
        c.id.startsWith(rolePrefixForUser) &&
        c.application === "Small" &&
        c.activity === "Delete" &&
        c.state === rs
    );
  };

  const getButtonText = (reqState) => {
    if (!reqState) return "New Request";
    const stop = exceptionTimelines.find(
      (e) => e.id.startsWith("stop") && e.requestState === reqState
    );
    if (stop) return stop.showState1;
    const pause = exceptionTimelines.find(
      (e) => e.id.startsWith("pause") && e.requestState === reqState
    );
    if (pause) return pause.showState1;
    const change = exceptionTimelines.find(
      (e) => e.id.startsWith("change") && e.requestState === reqState
    );
    if (change) return reqState;
    const entry = otimelineData.find((e) => e.name === reqState);
    if (!entry) return reqState;
    const nextEntry = otimelineData.find(
      (e) => e.id === `id${parseInt(entry.id.replace("id", ""), 10) + 1}`
    );
    return nextEntry ? nextEntry.name : reqState;
  };

  const toggleStatusGroup = (statusKey) => {
    setExpandedStatuses((prev) => {
      // If null (all open by default), materialise full set first, then remove clicked key
      const base = prev === null
        ? new Set([
            ...enquiries.map(e => e.reqState || 'New Request'),
            ...orders.map(o => o.reqState || 'New Request'),
            ...oldOrders.map(o => o.reqState || 'New Request'),
          ])
        : new Set(prev);
      if (base.has(statusKey)) base.delete(statusKey);
      else base.add(statusKey);
      return base;
    });
  };

  const renderNormalView = () => {
    let allItems = [
      ...enquiries.map((e) => ({ ...e, _coll: "enquiry" })),
      ...orders.map((o) => ({ ...o, _coll: "order" })),
      ...oldOrders.map((o) => ({ ...o, _coll: "oldOrder" })),
    ].sort((a, b) => {
      const da = a.deliveryDate
        ? new Date(a.deliveryDate)
        : new Date(9999, 0, 1);
      const db2 = b.deliveryDate
        ? new Date(b.deliveryDate)
        : new Date(9999, 0, 1);
      return da - db2;
    });

    if (searchQuery && (userRole === "Super" || userRole === "Mover")) {
      const q = searchQuery.toLowerCase();
      allItems = allItems.filter((item) => {
        const userInfo = userNameMap[item.userId] || {};
        if (userInfo.name && userInfo.name.toLowerCase().includes(q))
          return true;
        if (userInfo.email && userInfo.email.toLowerCase().includes(q))
          return true;
        return Object.values(item).some((value) => {
          if (Array.isArray(value)) {
            return value.some((v) => {
              if (v && typeof v === "object" && !Array.isArray(v)) {
                return Object.values(v).some(
                  (subVal) => subVal && String(subVal).toLowerCase().includes(q)
                );
              }
              return v && String(v).toLowerCase().includes(q);
            });
          } else if (value && typeof value === "object") {
            return Object.values(value).some(
              (subVal) => subVal && String(subVal).toLowerCase().includes(q)
            );
          }
          return (
            value !== undefined &&
            value !== null &&
            String(value).toLowerCase().includes(q)
          );
        });
      });
    }

    // Build ordered status list from otimelineData + exception states
    const getStatusOrder = () => {
      const ordered = otimelineData
        .slice()
        .sort((a, b) => {
          const na = parseInt(a.id.replace("id", ""), 10);
          const nb = parseInt(b.id.replace("id", ""), 10);
          return na - nb;
        })
        .map((e) => e.name);
      // Append exception states not already in the ordered list
      exceptionTimelines.forEach((e) => {
        const s = e.requestState;
        if (s && !ordered.includes(s)) ordered.push(s);
      });
      // Fallback: "New Request" at front if not present
      if (!ordered.includes("New Request")) ordered.unshift("New Request");
      return ordered;
    };

    const statusOrder = getStatusOrder();

    // Group items by reqState
    const grouped = {};
    allItems.forEach((item) => {
      const s = item.reqState || "New Request";
      if (!grouped[s]) grouped[s] = [];
      grouped[s].push(item);
    });

    // Sort groups by statusOrder
    const sortedStatuses = Object.keys(grouped).sort((a, b) => {
      const ia = statusOrder.indexOf(a);
      const ib = statusOrder.indexOf(b);
      return (ia === -1 ? 9999 : ia) - (ib === -1 ? 9999 : ib);
    });

    const colSpan =
      userRole === "Buyer" ? 8 : userRole === "Super" || userRole === "Mover" ? 8 : 7;

    const renderTableRows = (items) =>
      items.map((profile) => {
        const isExpanded = selectedProfileId === profile.id;
        const rawCost =
          parseFloat(String(profile.ccost || "").replace(/[^0-9.]/g, "")) || 0;
        // Only add delivery cost when the base cost has been set (quote finalised).
        const rawDel = rawCost > 0
          ? parseFloat(String(profile.del_cost || "").replace(/[^0-9.]/g, "")) || 0
          : 0;
        const totalCost = rawCost + rawDel;
        const cost = totalCost > 0 ? `${getCurrencySymbol()}${totalCost}` : "";
        const formattedDate = profile.deliveryDate
          ? (() => {
              const [y, m, d] = profile.deliveryDate.split("-");
              return new Date(
                Number(y),
                Number(m) - 1,
                Number(d)
              ).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              });
            })()
          : "";
        const status = getButtonText(profile.reqState);
        const lastComment = (() => {
          const feed = (profile.rev_feed || "").trim();
          if (!feed) return null;
          const chunks = feed.split("\n\n").filter(Boolean);
          const raw = chunks[chunks.length - 1] || null;
          if (!raw) return null;
          return raw.replace(/^\[[^\]]+\]\s*/, "").trim() || null;
        })();
        return (
          <React.Fragment key={`${profile._coll}-${profile.id}`}>
            <tr
              className={`enq-row${isExpanded ? " enq-row-active" : ""}`}
              style={(() => {
                if (!profile.deliveryDate) return {};
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const [y, m, d] = profile.deliveryDate.split("-");
                const delDate = new Date(Number(y), Number(m) - 1, Number(d));
                if (delDate.getTime() === today.getTime())
                  return { backgroundColor: "#ffeaea" };
                if (delDate < today)
                  return { backgroundColor: "#ffbbbb" };
                return {};
              })()}
              onClick={() => handleProfileClick(profile)}
            >
              <td className="enq-td enq-td-img">
                {profile.images?.[0] ? (
                  <img
                    src={profile.images[0]}
                    alt=""
                    className="enq-thumb"
                    loading="lazy"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : null}
              </td>
              {(userRole === "Super" || userRole === "Mover") && (
                <td className="enq-td">
                  <span style={(userNameMap[profile.userId] || {}).env === 'Test' ? { color: 'orange' } : {}}>
                    {(userNameMap[profile.userId] || {}).name || ""}
                  </span>
                </td>
              )}
              <td className="enq-td">{formattedDate}</td>
              <td className="enq-td">{profile.deliveryTime || ""}</td>
              <td className="enq-td">
                {profile.quantity
                  ? `${String(profile.quantity).replace(/\s*kg$/i, "").trim()} Kilogram`
                  : ""}
              </td>
              <td className="enq-td">{cost}</td>
              <td
                className={`enq-td${
                  canEnableActionForProfile(profile) ? " enq-td-status" : ""
                }`}
              >
                {status}
              </td>
              {userRole === "Buyer" && (
                <td className="enq-td enq-td-comment">
                  {lastComment ? (
                    <span style={{ color: "#fa4848", fontSize: "12px" }}>
                      {lastComment}
                    </span>
                  ) : (
                    ""
                  )}
                </td>
              )}
              <td
                className="enq-td enq-td-del"
                onClick={(e) => e.stopPropagation()}
              >
                {canEnableDeleteForProfile(profile) && (
                  <button
                    className="enq-del-btn"
                    onClick={() =>
                      handleDelete(profile.id, profile._coll, profile.userId)
                    }
                  >
                    <FaTrash />
                  </button>
                )}
              </td>
            </tr>
            {isExpanded && (
              <tr className="enq-expanded-row">
                <td colSpan={colSpan} className="enq-expanded-td">
                  <EnquiryLargeCard
                    profile={profile}
                    user={user}
                    userRole={userRole}
                    enquiryId={profile.id}
                    collectionName={profile._coll}
                    onClose={() => setSelectedProfileId(null)}
                    onProfileMoved={handleProfileMoved}
                  />
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      });

    const isOrderCollection = (statusKey) => {
      if (!otimelineData.length || orderReceivedIdNumber === null) return false;
      const entry = otimelineData.find((e) => e.name === statusKey);
      if (!entry) return false;
      return parseInt(entry.id.replace("id", ""), 10) >= orderReceivedIdNumber;
    };

    return (
      <div className="enquiry-page">
        {/* Top controls row: ? toggle + search (same row) */}
        <div className="enq-top-controls">
          {(userRole === "Buyer" || userRole === "Super") && (
            <button
              className="enq-pf-toggle enq-pf-toggle--inline"
              onClick={() => setShowProcessFlow((v) => !v)}
              title="Order process guide"
            >
              {showProcessFlow ? (
                <span className="enq-pf-toggle-arrow">&#9664;</span>
              ) : (
                <span className="enq-pf-toggle-q">?</span>
              )}
            </button>
          )}
          {(userRole === "Super" || userRole === "Mover") && (
            <div className="enq-search-wrapper">
              <input
                type="text"
                className="enq-search-input"
                placeholder="Search enquiries and orders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="enq-search-clear-btn"
                  onClick={() => setSearchQuery("")}
                >
                  <FaTimes />
                </button>
              )}
            </div>
          )}
        </div>

        <div className="enq-main-layout">
          {(userRole === "Buyer" || userRole === "Super") && (
            <div
              className={`enq-pf-wrapper${
                showProcessFlow ? " enq-pf-wrapper--open" : ""
              }`}
            >
              <div className="enq-process-flow">
                {[
                  { label: "Request Review", sub: "Fill delivery details", action: true },
                  { label: "Review Pending", sub: "Under review", action: false },
                  { label: "Review In-Progress", sub: "Quote being prepared", action: false },
                  { label: "Review Feedback", sub: "Accept or reject quote", action: true },
                  { label: "Order In-Progress", sub: "In preparation", action: false },
                  { label: "Delivery In-Progress", sub: "On the way", action: false },
                  { label: "Give Feedback", sub: "Rate your experience", action: true },
                  { label: "Order Delivered", sub: "All done!", action: false, final: true },
                ].map((step, i, arr) => (
                  <React.Fragment key={step.label}>
                    <div
                      className={`enq-pf-step${step.action ? " enq-pf-step-action" : ""}${step.final ? " enq-pf-step-final" : ""}`}
                    >
                      <div className="enq-pf-dot">
                        {step.final ? "?" : step.action ? "?" : i + 1}
                      </div>
                      <div className="enq-pf-label">{step.label}</div>
                      <div className="enq-pf-sub">{step.sub}</div>
                    </div>
                    {i < arr.length - 1 && (
                      <div className="enq-pf-arrow">{"\u2192"}</div>
                    )}
                  </React.Fragment>
                ))}
                {(() => {
                  if (!otimelineData.length || !exceptionTimelines.length)
                    return null;
                  const diagramSteps = [
                    "Request Review", "Review Pending", "Review In-Progress",
                    "Review Feedback", "Order In-Progress", "Delivery In-Progress",
                    "Give Feedback", "Order Delivered",
                  ];
                  const diagramStepSet = new Set(diagramSteps);
                  const getIdNum = (name) => {
                    const entry = otimelineData.find((e) => e.name === name);
                    return entry ? parseInt(entry.id.replace("id", ""), 10) : -1;
                  };
                  const diagramIdNums = diagramSteps
                    .map(getIdNum)
                    .filter((n) => n !== -1)
                    .sort((a, b) => a - b);
                  const exceptionStepNum = (requestState) => {
                    const n = getIdNum(requestState);
                    if (n === -1) return null;
                    const count = diagramIdNums.filter((d) => d <= n).length;
                    const offset = diagramStepSet.has(requestState) ? 0 : 1;
                    return count + 1 + offset;
                  };
                  const normalTransitions = new Set(
                    exceptionTimelines
                      .filter((e) => e.id.startsWith("change") && e.showState1)
                      .map((e) => e.showState1)
                  );
                  const isNormalState = (s) =>
                    diagramStepSet.has(s) || normalTransitions.has(s);
                  const exMap = {};
                  exceptionTimelines.forEach((e) => {
                    if (!e.id.startsWith("change") && !e.id.startsWith("option")) return;
                    const stepNum = exceptionStepNum(e.requestState);
                    if (!stepNum) return;
                    const candidates = e.id.startsWith("change")
                      ? [e.showState2]
                      : [e.showState1, e.showState2];
                    candidates.forEach((state) => {
                      if (!state || isNormalState(state)) return;
                      if (!exMap[state]) exMap[state] = { label: state, minStep: stepNum };
                      else if (stepNum < exMap[state].minStep) exMap[state].minStep = stepNum;
                    });
                  });
                  const exList = Object.values(exMap).sort((a, b) => a.minStep - b.minStep);
                  if (!exList.length) return null;
                  return (
                    <div className="enq-pf-exceptions">
                      {exList.map((ex) => (
                        <div
                          key={ex.label}
                          className={`enq-pf-ex-item enq-pf-ex-${/cancel/i.test(ex.label) ? "cancel" : "hold"}`}
                        >
                          <div className="enq-pf-ex-dot">{ex.minStep}</div>
                          <div className="enq-pf-ex-info">
                            <div className="enq-pf-ex-label">{ex.label}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          <div className="enq-content-area">
            {sortedStatuses.length === 0 ? (
              <p className="enquiry-no-match-message">
                No enquiries or orders found.
              </p>
            ) : (
              <div className="enq-table-scroll">
                {sortedStatuses.map((statusKey) => {
                  const items = grouped[statusKey];
                  if (!items || items.length === 0) return null;
                  const isOpen = expandedStatuses === null || expandedStatuses.has(statusKey);
                  return (
                    <div key={statusKey} className="enq-status-group">
                      <div className="enq-status-header">
                        <h3 className="enq-status-title">
                          {getButtonText(statusKey)}
                        </h3>
                        <button
                          className="enq-status-toggle-btn"
                          onClick={() => toggleStatusGroup(statusKey)}
                        >
                          {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                      {isOpen && (
                        <div className="enq-status-content">
                          <table className="enq-table">
                            <thead>
                              <tr>
                                <th className="enq-th enq-th-img"></th>
                                {(userRole === "Super" || userRole === "Mover") && (
                                  <th className="enq-th">Name</th>
                                )}
                                <th className="enq-th">Delivery Date</th>
                                <th className="enq-th">Delivery Time</th>
                                <th className="enq-th">Quantity</th>
                                <th className="enq-th">Cost</th>
                                <th className="enq-th">Status</th>
                                {userRole === "Buyer" && (
                                  <th className="enq-th">Comment</th>
                                )}
                                <th className="enq-th"></th>
                              </tr>
                            </thead>
                            <tbody>{renderTableRows(items)}</tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

    if (searchQuery && (userRole === "Super" || userRole === "Mover")) {
      const q = searchQuery.toLowerCase();
      allItems = allItems.filter((item) => {
        const userInfo = userNameMap[item.userId] || {};
        if (userInfo.name && userInfo.name.toLowerCase().includes(q))
          return true;
        if (userInfo.email && userInfo.email.toLowerCase().includes(q))
          return true;
        return Object.values(item).some((value) => {
          if (Array.isArray(value)) {
            return value.some((v) => {
              if (v && typeof v === "object" && !Array.isArray(v)) {
                return Object.values(v).some(
                  (subVal) => subVal && String(subVal).toLowerCase().includes(q)
                );
              }
              return v && String(v).toLowerCase().includes(q);
            });
          } else if (value && typeof value === "object") {
            return Object.values(value).some(
              (subVal) => subVal && String(subVal).toLowerCase().includes(q)
            );
          }
          return (
            value !== undefined &&
            value !== null &&
            String(value).toLowerCase().includes(q)
          );
        });
      });
    }

  if (!user) {
    return <p>Please log-in to view your enquiries.</p>;
  }

  if (loading) {
    return <p>Loading...</p>;
  }
  if (error) {
    return <p className="error">{error}</p>;
  }

  return renderNormalView();
};
export default Enquiry;

